import { CisdEngine } from "./CisdEngine";
import { DEFAULT_T50_CONFIG, T50Engine } from "../strategies/t50/T50Engine";
import type { Candle, Timeframe } from "../types";
import type { AlertFire } from "../alerts/types";
import { EventBus } from "../core/EventBus";
import { BOSEngine } from "./BOSEngine";
import { SwingEngine } from "./SwingEngine";
import { DEFAULT_2B1A_CONFIG, TwoBOneAEngine } from "./twoBOneA/TwoBOneAEngine";
import type { TwoBOneAEvent } from "./twoBOneA/TwoBOneAEngine";
import { FVGEngine, LiquidityEngine, OrderBlockEngine } from "./ZoneEngines";
import { DEFAULT_STRUCTURE_SETTINGS, StructureEvent, StructureFlags, StructureResult, StructureSettings } from "./types";

export interface StructureSignal { symbol: string; tf: Timeframe; event: StructureEvent }
export interface AlertPreviewSignal { symbol: string; tf: Timeframe; fire: AlertFire; replay: true }
export interface TwoBOneASignal { symbol: string; tf: Timeframe; event: TwoBOneAEvent }

// Analysis bus: structure events + replay alert previews + 2B1A swings fan out to signals panel, alerts, strategies…
export const analysisBus = new EventBus<{ structure: StructureSignal; alertPreview: AlertPreviewSignal; twoBOneA: TwoBOneASignal }>();

// Swing Detector → Structure Map → BOS/CHoCH → FVG / OB → Liquidity Map · 2B1A runs as an independent structure model
export const StructureEngine = {
  analyze(candles: Candle[], flags: StructureFlags, settings: StructureSettings = DEFAULT_STRUCTURE_SETTINGS): StructureResult | null {
    if (!candles.length || !Object.values(flags).some(Boolean)) return null;
    const swings = SwingEngine.find(candles, settings.lookback);
    const { events, trend } = BOSEngine.detect(candles, swings, settings.lookback);
    let twoBOneA: StructureResult["twoBOneA"] = null;
    if (flags.twoBOneA) {
      const cfg = { ...DEFAULT_2B1A_CONFIG, mode: settings.twoBOneAMode ?? "ohlc" };
      if (settings.twoBOneAState) { const u = settings.twoBOneAState.update(candles, cfg); twoBOneA = { ...u.result, fresh: u.fresh }; }
      else twoBOneA = { ...TwoBOneAEngine.find(candles, cfg), fresh: [] };
    }
    return {
      highs: swings.highs,
      lows: swings.lows,
      events,
      trend,
      fvg: flags.fvg ? FVGEngine.find(candles) : [],
      obs: flags.ob ? OrderBlockEngine.find(candles, events) : [],
      liquidity: flags.liq ? LiquidityEngine.find(candles, swings, settings.eqTolerance) : [],
      cisd: flags.cisd ? CisdEngine.analyze(candles) : null,
      t50: flags.t50 ? T50Engine.analyze(candles, { ...DEFAULT_T50_CONFIG, ...(settings.t50 || {}) }) : null,
      twoBOneA,
    };
  },

  // Emits events that are new since `prevLastTime` (returns the new watermark). 2B1A fresh events ride along.
  publish(symbol: string, tf: Timeframe, result: StructureResult | null, prevLastTime: number): number {
    if (!result) return prevLastTime;
    let mark = prevLastTime;
    for (const ev of result.events) {
      if (ev.time <= prevLastTime) continue;
      analysisBus.emit("structure", { symbol, tf, event: ev });
      if (ev.time > mark) mark = ev.time;
    }
    for (const ev of result.twoBOneA?.fresh ?? []) analysisBus.emit("twoBOneA", { symbol, tf, event: { ...ev, symbol, timeframe: tf.id } });
    return mark;
  },
};
